import { useState } from "react";
import {
  keepPreviousData,
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { isProblem, ProblemError, PROBLEM_TYPES, type components } from "@jbnl/api-client";
import { alsGetal } from "@/features/reistijden/useReistijden";
import { seizoenenKey } from "@/features/session/useSeizoenen";
import i18n from "@/i18n";
import { api } from "@/lib/api";
import { UNPAGINATED_FETCH_SIZE } from "@/lib/paging";

export const niveausKey = ["beheer", "niveaus"] as const;
export const klassenKey = ["beheer", "klassen"] as const;
export const korpsenKey = ["beheer", "korpsen"] as const;
export const wedstrijddagenKey = (seizoenId: string) =>
  ["beheer", "wedstrijddagen", seizoenId] as const;
export const wedstrijdleiderKandidatenKey = (wedstrijddagId: string) =>
  ["beheer", "wedstrijdleider-kandidaten", wedstrijddagId] as const;
export const wedstrijdleiderKandidatenVoorKorpsKey = (korpsId: string) =>
  ["beheer", "wedstrijdleider-kandidaten-voor-korps", korpsId] as const;

export type Seizoen = components["schemas"]["SeizoenDto"];
export type Wedstrijddag = components["schemas"]["WedstrijddagDto"];
export type Wedstrijdbaan = components["schemas"]["WedstrijdbaanDto"];
export type Meldadres = components["schemas"]["MeldadresDto"];
export type Korps = components["schemas"]["KorpsDto"];
export type Speelschemaregel = components["schemas"]["SpeelschemaregelDto"];
export type Promotieplaats = components["schemas"]["PromotieplaatsDto"];
/** `reistijdSeconds` is narrowed here the same way `useReistijden` narrows `Seconds`. */
export type WedstrijdleiderKandidaat = Omit<
  components["schemas"]["WedstrijdleiderKandidaatDto"],
  "reistijdSeconds"
> & { reistijdSeconds: number | null };

/** A null `id` tells the server the row is new; one that keeps its id is retimed, and the ploeg
 * standing in it moves along (ADR-0039). */
export type Regelinvoer = { id: string | null; time: string; label: string | null };

/**
 * RFC 7807, so a refusal carries a Dutch sentence; the status code is the fallback. The type
 * comes along so a caller can act on which rule turned it away instead of matching the sentence.
 */
function problem(error: unknown, response: Response, fallback: string) {
  const { title, type } = (error ?? {}) as { title?: string; type?: string };
  return new ProblemError(title ?? `${fallback} (${response.status}).`, type);
}

export function useNiveaus() {
  return useQuery({
    queryKey: niveausKey,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/beheer/niveaus");
      if (!data) throw problem(error, response, i18n.t("wedstrijden:errors.niveaus"));
      return data;
    },
  });
}

export function useKlassen() {
  return useQuery({
    queryKey: klassenKey,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/beheer/klassen");
      if (!data) throw problem(error, response, i18n.t("wedstrijden:errors.klassen"));
      return data;
    },
  });
}

/**
 * Its own read rather than `me.korpsen`, which carries no address and answers a different
 * question. One big page: ~154 korpsen nationally, well under `UNPAGINATED_FETCH_SIZE`.
 */
export function useBeheerKorpsen() {
  return useQuery({
    queryKey: korpsenKey,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/beheer/korpsen", {
        params: { query: { pageSize: UNPAGINATED_FETCH_SIZE } },
      });
      if (!data) throw problem(error, response, i18n.t("wedstrijden:errors.korpsen"));
      return data.items;
    },
  });
}

export const korpsenPagedKey = (
  page: number | undefined,
  search: string,
  sortBy: string | undefined,
  sortDescending: boolean,
  inschrijfperiodeId: string | undefined,
  reedsIngeschreven: string | undefined,
) =>
  [
    ...korpsenKey,
    "paged",
    page,
    search,
    sortBy,
    sortDescending,
    inschrijfperiodeId,
    reedsIngeschreven,
  ] as const;

/**
 * The Korpsen (Ploegen) list page's own read: one page, searched and sorted on the server.
 * `inschrijfperiodeId`/`reedsIngeschreven` are the "already entered" filter (#198).
 */
export function useBeheerKorpsenPaged(
  page: number | undefined,
  pageSize: number,
  search: string,
  sortBy: string | undefined,
  sortDescending: boolean,
  inschrijfperiodeId: string | undefined,
  reedsIngeschreven: string | undefined,
) {
  return useQuery({
    queryKey: korpsenPagedKey(
      page,
      search,
      sortBy,
      sortDescending,
      inschrijfperiodeId,
      reedsIngeschreven,
    ),
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/beheer/korpsen", {
        params: {
          query: {
            page,
            pageSize,
            search: search || undefined,
            sortBy,
            sortDescending,
            inschrijfperiodeId,
            reedsIngeschreven,
          },
        },
      });
      if (!data) throw problem(error, response, i18n.t("wedstrijden:errors.korpsen"));
      return data;
    },
    // Each page is its own query key, so without this a click to a page never fetched before
    // clears `data` and swaps the table for a skeleton - unmounting the very pager button just
    // clicked, which loses focus and (in some browsers) scrolls the page back to the top. The
    // previous page's rows stay on screen, correct but stale, until the new ones arrive.
    placeholderData: keepPreviousData,
  });
}

/**
 * One big page, since a seizoen holds a handful of dagen. The list page drives real paging
 * through `useWedstrijddagenPaged` instead.
 */
export function useWedstrijddagen(seizoenId: string | undefined) {
  return useQuery({
    // Only asked once a seizoen is chosen: without one there is no list to draw, and a query
    // keyed on undefined would cache an answer for a question nobody asked.
    queryKey: wedstrijddagenKey(seizoenId ?? ""),
    queryFn: !seizoenId
      ? skipToken
      : async () => {
          const { data, error, response } = await api.GET(
            "/api/beheer/seizoenen/{seizoenId}/wedstrijddagen",
            { params: { path: { seizoenId }, query: { pageSize: UNPAGINATED_FETCH_SIZE } } },
          );
          if (!data) throw problem(error, response, i18n.t("wedstrijden:errors.dagen"));
          return data.items;
        },
  });
}

export const wedstrijddagenPagedKey = (
  seizoenId: string,
  page: number | undefined,
  search: string,
  sortBy: string | undefined,
  sortDescending: boolean,
  niveauId: string | undefined,
  datumVan: string | undefined,
  datumTot: string | undefined,
) =>
  [
    ...wedstrijddagenKey(seizoenId),
    "paged",
    page,
    search,
    sortBy,
    sortDescending,
    niveauId,
    datumVan,
    datumTot,
  ] as const;

/** The Wedstrijddagen list page's own read: one page, searched, filtered and sorted on the server. */
export function useWedstrijddagenPaged(
  seizoenId: string | undefined,
  page: number | undefined,
  pageSize: number,
  search: string,
  sortBy: string | undefined,
  sortDescending: boolean,
  niveauId: string | undefined,
  datumVan: string | undefined,
  datumTot: string | undefined,
) {
  return useQuery({
    queryKey: wedstrijddagenPagedKey(
      seizoenId ?? "",
      page,
      search,
      sortBy,
      sortDescending,
      niveauId,
      datumVan,
      datumTot,
    ),
    queryFn: !seizoenId
      ? skipToken
      : async () => {
          const { data, error, response } = await api.GET(
            "/api/beheer/seizoenen/{seizoenId}/wedstrijddagen",
            {
              params: {
                path: { seizoenId },
                query: {
                  page,
                  pageSize,
                  search: search || undefined,
                  sortBy,
                  sortDescending,
                  niveauId,
                  datumVan,
                  datumTot,
                },
              },
            },
          );
          if (!data) throw problem(error, response, i18n.t("wedstrijden:errors.dagen"));
          return data;
        },
    // Each page is its own query key, so without this a click to a page never fetched before
    // clears `data` and swaps the table for a skeleton - unmounting the very pager button just
    // clicked, which loses focus and (in some browsers) scrolls the page back to the top. The
    // previous page's rows stay on screen, correct but stale, until the new ones arrive.
    placeholderData: keepPreviousData,
  });
}

export function useCreateSeizoen() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: { name: string; startDate: string; endDate: string }) => {
      const { data, error, response } = await api.POST("/api/beheer/seizoenen", { body });
      if (!data) throw problem(error, response, i18n.t("wedstrijden:errors.aanmaken"));
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: seizoenenKey }),
  });
}

export function useUpdateSeizoen() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...body
    }: {
      id: string;
      name: string;
      startDate: string;
      endDate: string;
    }) => {
      const { data, error, response } = await api.PUT("/api/beheer/seizoenen/{id}", {
        params: { path: { id } },
        body,
      });
      if (!data) throw problem(error, response, i18n.t("wedstrijden:errors.opslaan"));
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: seizoenenKey }),
  });
}

export function useDeleteSeizoen() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error, response } = await api.DELETE("/api/beheer/seizoenen/{id}", {
        params: { path: { id } },
      });
      if (!response.ok) throw problem(error, response, i18n.t("wedstrijden:errors.verwijderen"));
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: seizoenenKey }),
  });
}

/** Both null until a host is appointed: a dag publishes its host's address, and the server
 * refuses one without the other. */
type DagVelden = {
  date: string;
  niveauId: string;
  organiserendKorpsId: string | null;
  meldadres: Meldadres | null;
  /** Set on the resubmit after the beheerder has seen and dismissed the duplicate warning (#179). */
  acknowledgeDuplicateWarning?: boolean;
};

/**
 * What creating a dag takes on top of editing one. `klasseIds` is one entry per baan and not a
 * set, because a baan is something ploegen stand on rather than a count (#60). All three may be
 * empty and are corrected on the dag's own screen, which is why the edit path drops them (ADR-0030).
 */
type NieuweDagVelden = DagVelden & {
  klasseIds: string[];
  wedstrijdleider: string | null;
  telefoonnummer: string | null;
};

export function useCreateWedstrijddag(seizoenId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (velden: NieuweDagVelden) => {
      const { acknowledgeDuplicateWarning = false, ...body } = velden;
      const { data, error, response } = await api.POST(
        "/api/beheer/seizoenen/{seizoenId}/wedstrijddagen",
        { params: { path: { seizoenId } }, body: { ...body, acknowledgeDuplicateWarning } },
      );
      if (!data) throw problem(error, response, i18n.t("wedstrijden:errors.aanmaken"));
      return data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: wedstrijddagenKey(seizoenId) }),
  });
}

export function useUpdateWedstrijddag(seizoenId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      acknowledgeDuplicateWarning = false,
      ...body
    }: DagVelden & { id: string }) => {
      const { data, error, response } = await api.PUT("/api/beheer/wedstrijddagen/{id}", {
        params: { path: { id } },
        body: { ...body, acknowledgeDuplicateWarning },
      });
      if (!data) throw problem(error, response, i18n.t("wedstrijden:errors.opslaan"));
      return data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: wedstrijddagenKey(seizoenId) }),
  });
}

export function useDeleteWedstrijddag(seizoenId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error, response } = await api.DELETE("/api/beheer/wedstrijddagen/{id}", {
        params: { path: { id } },
      });
      if (!response.ok) throw problem(error, response, i18n.t("wedstrijden:errors.verwijderen"));
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: wedstrijddagenKey(seizoenId) }),
  });
}

/**
 * What the wedstrijdboekje prints per wedstrijddag and nothing else in the tool holds. Its own
 * mutation rather than more fields on the dag form: a dag is entered from a calendar months
 * before a wedstrijdleider has a briefingtijd or a number to be reached on.
 */
export function useSetBoekjegegevens(seizoenId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...body
    }: {
      id: string;
      briefing: string | null;
      opmerkingen: string[];
      telefoonnummer: string | null;
      wedstrijdleider: string | null;
      meldtijd: string | null;
    }) => {
      const { data, error, response } = await api.PUT(
        "/api/beheer/wedstrijddagen/{id}/boekjegegevens",
        { params: { path: { id } }, body },
      );
      if (!data) throw problem(error, response, i18n.t("wedstrijden:errors.opslaan"));
      return data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: wedstrijddagenKey(seizoenId) }),
  });
}

/** `reistijdSeconds` is narrowed the same way `useReistijden` narrows `Seconds`. */
function naarWedstrijdleiderKandidaten(
  data: components["schemas"]["WedstrijdleiderKandidaatDto"][],
): WedstrijdleiderKandidaat[] {
  return data.map((kandidaat) => ({
    ...kandidaat,
    reistijdSeconds: kandidaat.reistijdSeconds == null ? null : alsGetal(kandidaat.reistijdSeconds),
  }));
}

/**
 * Every Beheerder the wedstrijdleider picker offers for this dag, closest first (ADR-0072).
 * Disabled until the picker actually opens: the list is small but still a request nobody needs
 * on every Boekjegegevens render.
 */
export function useWedstrijdleiderKandidaten(wedstrijddagId: string, enabled: boolean) {
  return useQuery({
    queryKey: wedstrijdleiderKandidatenKey(wedstrijddagId),
    enabled,
    queryFn: async (): Promise<WedstrijdleiderKandidaat[]> => {
      const { data, error, response } = await api.GET(
        "/api/beheer/wedstrijddagen/{id}/wedstrijdleider-kandidaten",
        { params: { path: { id: wedstrijddagId } } },
      );
      if (!data) throw problem(error, response, i18n.t("wedstrijden:errors.wedstrijdleiderKandidaten"));
      return naarWedstrijdleiderKandidaten(data);
    },
  });
}

/**
 * The same picker's candidates for a wedstrijddag that does not exist yet: the create form knows
 * a korps before it knows a dag, and an unoverridden meldadres routes to that korps's own point.
 */
export function useWedstrijdleiderKandidatenVoorKorps(korpsId: string, enabled: boolean) {
  return useQuery({
    queryKey: wedstrijdleiderKandidatenVoorKorpsKey(korpsId),
    enabled,
    queryFn: async (): Promise<WedstrijdleiderKandidaat[]> => {
      const { data, error, response } = await api.GET(
        "/api/beheer/korpsen/{korpsId}/wedstrijdleider-kandidaten",
        { params: { path: { korpsId } } },
      );
      if (!data) throw problem(error, response, i18n.t("wedstrijden:errors.wedstrijdleiderKandidaten"));
      return naarWedstrijdleiderKandidaten(data);
    },
  });
}

/**
 * The speelschema of one or more banen of a dag, each as a whole list and all in one request.
 * The screen draws the dag's banen as columns of one table - the shape the boekje prints - so a
 * save that trimmed one column at a time would be several requests for one decision.
 */
export function useSetSpeelschema(seizoenId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      dagId,
      banen,
    }: {
      dagId: string;
      banen: { wedstrijdbaanId: string; regels: Regelinvoer[] }[];
    }) => {
      const { data, error, response } = await api.PUT(
        "/api/beheer/wedstrijddagen/{id}/speelschema",
        { params: { path: { id: dagId } }, body: { banen } },
      );
      if (!data) throw problem(error, response, i18n.t("wedstrijden:errors.speelschemaOpslaan"));
      return data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: wedstrijddagenKey(seizoenId) }),
  });
}

/**
 * The whole list per baan, so three plaatsen becoming two is one edit. On the baan and not the
 * dag, because a dag with two banen announces two different things (ADR-0062).
 */
export function useSetPromotieplaatsen(seizoenId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      baanId,
      plaatsen,
    }: {
      baanId: string;
      plaatsen: Promotieplaats[];
    }) => {
      const { data, error, response } = await api.PUT(
        "/api/beheer/banen/{id}/promotieplaatsen",
        { params: { path: { id: baanId } }, body: { plaatsen } },
      );
      if (!data) throw problem(error, response, i18n.t("wedstrijden:errors.promotiesOpslaan"));
      return data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: wedstrijddagenKey(seizoenId) }),
  });
}

/** Puts one baan back to the standaard for its klasse and niveau. Never silent - a beheerder asks. */
export function useHerstelSpeelschema(seizoenId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (baanId: string) => {
      const { data, error, response } = await api.POST(
        "/api/beheer/banen/{id}/speelschema/herstel",
        { params: { path: { id: baanId } } },
      );
      if (!data) throw problem(error, response, i18n.t("wedstrijden:errors.herstellen"));
      return data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: wedstrijddagenKey(seizoenId) }),
  });
}

/**
 * Both baan mutations answer with the whole dag rather than with the baan: adding or removing
 * one renumbers its klasse, so every other baan of that klasse can change what it is called.
 */
export function useAddWedstrijdbaan(seizoenId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      dagId,
      klasseId,
      acknowledgeDuplicateWarning = false,
    }: {
      dagId: string;
      klasseId: string;
      acknowledgeDuplicateWarning?: boolean;
    }) => {
      const { data, error, response } = await api.POST(
        "/api/beheer/wedstrijddagen/{id}/banen",
        { params: { path: { id: dagId } }, body: { klasseId, acknowledgeDuplicateWarning } },
      );
      if (!data) throw problem(error, response, i18n.t("wedstrijden:errors.baanToevoegen"));
      return data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: wedstrijddagenKey(seizoenId) }),
  });
}

/**
 * Tracks the "mogelijk duplicaat" refusal (#179) for one mutation: `vangOp` feeds it a failed
 * submit's variables and error from `onError`, opening the dialog only for that problem type, and
 * `doorgaan` hands back the same variables with the flag set so the caller can resubmit.
 */
export function useDuplicaatWaarschuwing<TVariables>() {
  const [waarschuwing, setWaarschuwing] = useState<{
    variables: TVariables;
    bericht: string;
  } | null>(null);

  return {
    bericht: waarschuwing?.bericht ?? null,
    vangOp(variables: TVariables, error: unknown) {
      if (isProblem(error, PROBLEM_TYPES.wedstrijddagMogelijkDuplicaat)) {
        setWaarschuwing({ variables, bericht: (error as Error).message });
      }
    },
    sluit: () => setWaarschuwing(null),
    doorgaan(): TVariables & { acknowledgeDuplicateWarning: true } {
      if (!waarschuwing) {
        throw new Error("Geen duplicaatwaarschuwing open om mee door te gaan.");
      }
      setWaarschuwing(null);
      return { ...waarschuwing.variables, acknowledgeDuplicateWarning: true };
    },
  };
}

export function useRemoveWedstrijdbaan(seizoenId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (baanId: string) => {
      const { data, error, response } = await api.DELETE("/api/beheer/banen/{id}", {
        params: { path: { id: baanId } },
      });
      if (!data) throw problem(error, response, i18n.t("wedstrijden:errors.baanVerwijderen"));
      return data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: wedstrijddagenKey(seizoenId) }),
  });
}
